import React, { forwardRef, useState } from 'react';
import { Image, StyleSheet, View } from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import PropTypes from 'prop-types';

import { Theme } from '@constants';
import { useTimeDuration } from '@hooks';

import { getThumbnailUrl } from '../../../helpers/StringUtils';
import { FocusableHighlight } from '../../focusable/FocusableHighlight/FocusableHighlight';
import { TWCText } from '../../primitives/TWCText/TWCText';

export const OnDemandShortFormItemLayout = {
  TILE_WIDTH: Theme.px(544),
  TILE_HEIGHT: Theme.px(400),
  TILE_SPACING: Theme.px(30),
};

export const OnDemandShortFormItemTile = forwardRef(
  (
    {
      accessibilityHint,
      accessibilityLabel,
      data,
      testID,
      onPress,
      nextFocusUp,
      nextFocusDown,
      nextFocusLeft,
      nextFocusRight,
      onFocus,
      focused: focusedProperty,
    },
    reference,
  ) => {
    const [focused, setFocused] = useState(focusedProperty ?? false); // focusedProperty for testing purposes.
    const { duration } = useTimeDuration(data?.duration, 'seconds');

    return (
      <FocusableHighlight
        accessibilityHint={accessibilityHint}
        accessibilityLabel={accessibilityLabel}
        nextFocusDown={nextFocusDown}
        nextFocusLeft={nextFocusLeft}
        nextFocusRight={nextFocusRight}
        nextFocusUp={nextFocusUp}
        ref={reference}
        testID={testID}
        underlayColor={Theme.transparent}
        onBlur={() => setFocused(false)}
        onFocus={event => {
          if (onFocus) {
            setFocused(true);
            onFocus(event);
          }
        }}
        onPress={onPress}
      >
        <View style={styles.container}>
          <View style={styles.thumbnail}>
            <Image source={{ uri: getThumbnailUrl(data?.thumb) }} style={styles.image} />
          </View>
          <LinearGradient
            colors={[Theme.transparent, Theme.withOpacity(Theme.black, 0.1), Theme.withOpacity(Theme.black, 0.9)]}
            style={styles.gradientContainer}
          />
          {focused ? <View style={styles.thumbnailFocused} /> : null}
          <View style={styles.durationContainer}>
            <TWCText noTestID fontFamily={'medium'} style={[styles.durationText]}>
              {duration}
            </TWCText>
          </View>
          <View style={styles.boxBottom}>
            <TWCText noTestID fontFamily={'medium'} numberOfLines={2} style={[styles.titleText]}>{`${
              data?.title ?? '---'
            }`}</TWCText>
          </View>
        </View>
      </FocusableHighlight>
    );
  },
);

OnDemandShortFormItemTile.propTypes = {
  accessibilityLabel: PropTypes.string,
  accessibilityHint: PropTypes.string,
  data: PropTypes.object,
  testID: PropTypes.string,
  onPress: PropTypes.func,
  nextFocusUp: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
  nextFocusDown: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
  nextFocusLeft: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
  nextFocusRight: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
  onFocus: PropTypes.func,
  focused: PropTypes.bool,
};

OnDemandShortFormItemTile.defaultProps = {
  accessibilityLabel: null,
  accessibilityHint: null,
  data: null,
  testID: null,
  onPress: () => {},
  nextFocusUp: null,
  nextFocusDown: null,
  nextFocusLeft: null,
  nextFocusRight: null,
  onFocus: () => {},
  focused: false,
};

OnDemandShortFormItemTile.displayName = 'OnDemandShortFormItemTile';

const styles = StyleSheet.create({
  boxBottom: {
    height: Theme.px(168),
    justifyContent: 'space-between',
    padding: Theme.px(12),
  },
  container: {
    backgroundColor: Theme.withOpacity(Theme.black, 0.6),
    borderRadius: Theme.tileBorderRadius,
    height: OnDemandShortFormItemLayout.TILE_HEIGHT,
    overflow: 'hidden',
    width: OnDemandShortFormItemLayout.TILE_WIDTH,
  },
  durationContainer: {
    backgroundColor: Theme.black,
    borderRadius: Theme.tileBorderRadius,
    bottom: Theme.px(114),
    paddingHorizontal: Theme.px(12),
    paddingVertical: Theme.px(4),
    position: 'absolute',
    right: Theme.px(18),
  },
  durationText: {
    color: Theme.whisperWhite,
    fontSize: Theme.px(29),
  },
  gradientContainer: {
    height: Theme.px(306),
    left: 0,
    position: 'absolute',
    right: 0,
  },
  image: {
    borderRadius: Theme.tileBorderRadius,
    flex: 1,
    zIndex: 0,
  },
  thumbnail: {
    height: Theme.px(306),
    width: Theme.px(544),
  },
  thumbnailFocused: {
    borderColor: Theme.white,
    borderRadius: Theme.tileBorderRadius,
    borderWidth: Theme.px(6),
    height: Theme.px(306),
    overflow: 'hidden',
    position: 'absolute',
    top: 0,
    width: Theme.px(544),
  },
  timeAgoText: {
    color: Theme.withOpacity(Theme.whisperWhite, 0.5),
    fontSize: Theme.px(30),
  },
  titleText: {
    color: Theme.whisperWhite,
    fontSize: Theme.px(30),
  },
});
